import { ActionType, Armor, Cape, Color, Eyes, Hair, Nose, Pants, Race, Sex, Shield, Shirt, Shoe, Weapon } from "../character/character"
import { Bauldron } from "../item/item_type"
import { Appearance, Dimensions } from "../models"
import {
    LpcBodyDescriptor,
    LpcCharacterDescriptorCollection,
    LpcComponentDescriptor,
    LpcSheetCollection,
    LpcSpritesheetDescriptors,
} from "./lpc"
import { expandEntry } from "./registry"
import { SPRITE_REGISTRY } from "./sprite_registry.generated"
import { LpcSlot, resolveColor } from "./slot_colors"

export const getLpcBodyShadow = (race: Race) => SPRITE_REGISTRY.body?.[race]?.shadow

export const LpcRaceDimensions: Partial<Record<Race, Dimensions>> = {
    horse: {
        w: 128,
        h: 128,
    },
    snake: {
        w: 64,
        h: 64,
    },
    slime: {
        w: 64,
        h: 64,
    },
    wasp: {
        w: 32,
        h: 32,
    },
    spikes1: {
        w: 32,
        h: 32,
    },
    redspider: {
        w: 64,
        h: 64,
    },
    deer: {
        w: 128,
        h: 128,
    },
    rabbit: {
        w: 72,
        h: 72,
    },
    chicken: {
        w: 32,
        h: 32,
    },
}

/**
 * Every descriptor now comes from the registry. See `docs/sprite-lab.md` for the whole pipeline.
 *
 * What used to be here was 600 lines of `if (component === ...)` whose real content was a set of
 * path templates and, per component, a translation from the game's `Color` names to the ones the
 * art files actually use (`brown` -> `dark_brown`, `white` -> `beige`, `gold` -> `8`). That table
 * is data, and it now lives in `sprite_registry.json` with a generated module beside it.
 *
 * The registry was DERIVED from this file's own answers over every descriptor the enums can
 * produce and verified by re-expanding all of them, so the swap is a capture rather than a
 * rewrite. `lpc_forge.test.ts` holds the snapshot of all 2,303 cases that proves it, and
 * `yarn derive:lpc --check` proves the registry still matches its source.
 *
 * The one rule that stayed in code is the colour fallback -- a component asked for a colour it
 * has no art for renders its first one -- because it is identical across every slot and depends
 * on the enum's declaration order. See `slot_colors.ts`.
 *
 * Behaviour note: an unknown component used to throw here for some slots (reading `colors[0]` off
 * an undefined colour list) and return `[]` for others. It is `[]` everywhere now. Saved
 * characters outlive the enums, and a removed hair type should cost that layer, not the character.
 */
const fromRegistry = (slot: LpcSlot, component: string, color: Color, sex: Sex): LpcSheetCollection => {
    const entry = SPRITE_REGISTRY[slot]?.[component]
    if (!entry) {
        return []
    }
    return expandEntry(entry, {
        color: resolveColor(slot, component, color),
        requestedColor: color,
        sex,
    })
}

/** Slots whose descriptor is `{component, color}` plus a sex -- everything except the body. */
const forgeComponent =
    (slot: LpcSlot) =>
    (descriptor: LpcComponentDescriptor<any>, sex: Sex): LpcSheetCollection =>
        descriptor ? fromRegistry(slot, descriptor.component, descriptor.color, sex) : []

export const forgeLpcBodySpritesheetDescriptors = (body: LpcBodyDescriptor): LpcSheetCollection =>
    body ? fromRegistry("body", body.race, body.color, body.sex) : []

export const getWeaponToOversizeState = (weapon: Weapon) => {
    return ["longsword", "rapier", "mace", "scythe", "saber", "flail", "halberd", "waraxe"].includes(weapon)
}

export const forgeLpcWeaponSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Weapon>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("weapon")(descriptor, sex)

export const forgeLpcHairSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Hair>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("hair")(descriptor, sex)

export const forgeLpcNoseSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Nose>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("nose")(descriptor, sex)

export const forgeLpcEyesSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Eyes>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("eyes")(descriptor, sex)

export const forgeLpcPantsSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Pants>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("pants")(descriptor, sex)

export const forgeLpcShirtSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Shirt>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("shirt")(descriptor, sex)

export const forgeLpcArmorSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Armor>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("armor")(descriptor, sex)

export const forgeLpcShoeSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Shoe>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("shoes")(descriptor, sex)

export const forgeLpcShieldSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Shield>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("shield")(descriptor, sex)

export const forgeLpcCapeSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Cape>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("cape")(descriptor, sex)

export const forgeLpcBauldronSpritesheetDescriptors = (
    descriptor: LpcComponentDescriptor<Bauldron>,
    sex: Sex,
): LpcSheetCollection => forgeComponent("bauldron")(descriptor, sex)

export const defaultActionFor = (appearance: Appearance): ActionType => {
    if (appearance.race === "spikes1") {
        return "inactive"
    }
}

export const forgeLpcSpritesheetDescriptors = ({
    body,
    weapon,
    hair,
    nose,
    eyes,
    sex,
    pants,
    shirt,
    armor,
    shoes,
    shield,
    cape,
    bauldron,
}: LpcCharacterDescriptorCollection): LpcSpritesheetDescriptors => {
    const bodyDescriptors = forgeLpcBodySpritesheetDescriptors(body)
    const weaponDescriptors = forgeLpcWeaponSpritesheetDescriptors(weapon, sex || body.sex)
    const hairDescriptors = forgeLpcHairSpritesheetDescriptors(hair, sex || body.sex)
    const noseDescriptors = forgeLpcNoseSpritesheetDescriptors(nose, sex || body.sex)
    const eyesDescriptors = forgeLpcEyesSpritesheetDescriptors(eyes, sex || body.sex)
    const pantsDescriptors = forgeLpcPantsSpritesheetDescriptors(pants, sex || body.sex)
    const shirtDescriptors = forgeLpcShirtSpritesheetDescriptors(shirt, sex || body.sex)
    const armorDescriptors = forgeLpcArmorSpritesheetDescriptors(armor, sex || body.sex)
    const shoeDescriptors = forgeLpcShoeSpritesheetDescriptors(shoes, sex || body.sex)
    const shieldDescriptors = forgeLpcShieldSpritesheetDescriptors(shield, sex || body.sex)
    const capeDescriptors = forgeLpcCapeSpritesheetDescriptors(cape, sex || body.sex)
    const bauldronDescriptors = forgeLpcBauldronSpritesheetDescriptors(bauldron, sex || body.sex)

    let descriptors: LpcSpritesheetDescriptors = {
        body: bodyDescriptors,
        weapon: weaponDescriptors,
        hair: hairDescriptors,
        pants: pantsDescriptors,
        shirt: shirtDescriptors,
        armor: armorDescriptors,
        shoes: shoeDescriptors,
        shield: shieldDescriptors,
        cape: capeDescriptors,
        bauldron: bauldronDescriptors,
        nose: noseDescriptors,
        eyes: eyesDescriptors,
    }

    return descriptors
}
